<template>
  <div
    class="guest-restriction-message__container"
    :class="{ 'guest-restriction-message__container--compact': compact }"
  >
    <div
      class="guest-restriction-message__panel"
      :class="{
        'guest-restriction-message__panel--page': !compact,
        'guest-restriction-message__panel--compact': compact,
      }"
    >
      <v-icon
        v-if="!compact"
        :icon="icon"
        :color="iconColor"
        size="64"
        class="mb-4"
      />

      <h2 v-if="!compact" class="text-h5 mb-3">{{ resolvedTitle }}</h2>

      <p
        class="guest-restriction-message__text"
        :class="compact ? 'text-body-2 text-medium-emphasis mb-4' : 'text-body-1 text-grey-darken-1 mb-6'"
      >
        {{ resolvedMessage }}
      </p>

      <slot name="preview" />

      <ColumnButtons
        :primary-text="registerButtonText"
        :primary-icon="compact ? '' : registerIcon"
        :primary-variant="compact ? 'flat' : 'elevated'"
        :primary-size="compact ? 'default' : 'large'"
        primary-block
        :dense="compact"
        :secondary-text="backButtonText"
        :secondary-size="compact ? 'default' : 'large'"
        :show-secondary="showBackButton"
        @primary-click="goToRegister"
        @secondary-click="goBack"
      >
        <template #between>
          <p class="guest-restriction-message__login-prompt text-body-2 text-medium-emphasis">
            ¿Ya tienes cuenta?
            <button
              type="button"
              class="guest-restriction-message__login-link"
              @click="goToLogin"
            >
              {{ loginButtonText }}
            </button>
          </p>
        </template>
      </ColumnButtons>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import ColumnButtons from '@/components/baseComponents/ColumnButtons.vue'

export const GUEST_RESTRICTION_COPY = {
  snapshots: {
    title: 'Guarda versiones y compáralas cuando quieras',
    message: 'Crea una cuenta gratuita para guardar y comparar versiones de tu obra.',
  },
  analysis: {
    title: 'Descubre el ritmo y las palabras de tu texto',
    message: 'Regístrate gratis para acceder al análisis de tu texto.',
  },
  exporter: {
    title: 'Descarga tu obra en PDF, EPUB o Word',
    message: 'Necesitas una cuenta gratuita para exportar tu obra.',
  },
  plot: {
    title: 'Organiza los arcos de tu historia',
    message: 'Crea tu cuenta para planificar las tramas de tu historia.',
  },
  'elements-graph': {
    title: 'Visualiza las relaciones de tu historia',
    message: 'Regístrate gratis para usar el grafo de relaciones.',
  },
  layouts: {
    title: 'Maqueta tus libros con estilos propios',
    message: 'Necesitas una cuenta gratuita para maquetar tus libros.',
  },
  images: {
    title: 'Gestiona las imágenes de tu obra',
    message: 'Crea una cuenta para gestionar las imágenes de tu obra.',
  },
  community: {
    title: 'Conecta con otros escritores',
    message: 'Regístrate para participar en la comunidad.',
  },
  profile: {
    title: 'Personaliza tu perfil público de escritor',
    message: 'Crea una cuenta gratuita para configurar y compartir tu perfil.',
  },
  account: {
    title: 'Gestiona tus datos y tu plan',
    message: 'Regístrate gratis para actualizar tu cuenta, revisar tu plan y administrar tus dispositivos.',
  },
  notifications: {
    title: 'Entérate cuando comentan tu historia',
    message: 'Crea tu cuenta gratuita para recibir notificaciones.',
  },
  challenges: {
    title: 'Fija metas y sigue tu progreso',
    message: 'Regístrate gratis para unirte a los desafíos de escritura.',
  },
  'writing-feedback': {
    title: 'Revisa tus hábitos de escritura',
    message: 'Necesitas una cuenta gratuita para ver tu resumen de escritura.',
  },
  'narrative-elements': {
    title: 'Organiza personajes, lugares y objetos',
    message: 'Crea tu cuenta para organizar los elementos narrativos.',
  },
} as const

export const GENERIC_GUEST_RESTRICTION_TITLE = 'Sigue escribiendo con una cuenta gratis'
export const GENERIC_GUEST_RESTRICTION_MESSAGE =
  'Esta funcionalidad requiere una cuenta gratuita. Regístrate para acceder.'

export const getGuestAuthRoute = (redirect: string) => ({
  path: '/login',
  query: {
    redirect,
  },
})

export default defineComponent({
  name: 'GuestRestrictionMessage',

  components: {
    ColumnButtons,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    feature: {
      type: String,
      default: '',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: 'mdi-account-lock',
    },
    iconColor: {
      type: String,
      default: 'orange',
    },
    registerButtonText: {
      type: String,
      default: 'Crear cuenta gratis',
    },
    registerIcon: {
      type: String,
      default: 'mdi-account-plus',
    },
    loginButtonText: {
      type: String,
      default: 'Inicia sesión',
    },
    loginIcon: {
      type: String,
      default: 'mdi-login',
    },
    showBackButton: {
      type: Boolean,
      default: true,
    },
    backButtonText: {
      type: String,
      default: 'Volver atrás',
    },
    backIcon: {
      type: String,
      default: 'mdi-arrow-left',
    },
    customBackAction: {
      type: Function,
      default: null,
    },
  },

  setup(props, { emit }) {
    const router = useRouter()
    const route = useRoute()

    const configuredCopy = computed(
      () => GUEST_RESTRICTION_COPY[props.feature as keyof typeof GUEST_RESTRICTION_COPY],
    )
    const resolvedTitle = computed(
      () => props.title || configuredCopy.value?.title || GENERIC_GUEST_RESTRICTION_TITLE,
    )
    const resolvedMessage = computed(
      () => props.message || configuredCopy.value?.message || GENERIC_GUEST_RESTRICTION_MESSAGE,
    )

    const goToRegister = () => {
      router.push(getGuestAuthRoute(route.fullPath))
    }

    const goToLogin = () => {
      router.push(getGuestAuthRoute(route.fullPath))
    }

    const goBack = () => {
      if (props.customBackAction) {
        props.customBackAction()
      } else {
        emit('back')
      }
    }

    return {
      goToLogin,
      goToRegister,
      goBack,
      resolvedTitle,
      resolvedMessage,
    }
  },
})
</script>

<style scoped>
.guest-restriction-message__container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  container: guest / inline-size;
}

.guest-restriction-message__container--compact {
  height: auto;
  min-height: 0;
  display: block;
}

.guest-restriction-message__panel--page {
  max-width: 1024px;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
  text-align: center;
  border-radius: 4px;
  box-shadow:
    0 2px 4px -1px rgba(0, 0, 0, 0.2),
    0 4px 5px 0 rgba(0, 0, 0, 0.14),
    0 1px 10px 0 rgba(0, 0, 0, 0.12);
  background: rgb(var(--v-theme-surface));
}

.guest-restriction-message__panel--compact {
  padding: 20px 16px 16px;
  text-align: center;
}

.guest-restriction-message__login-prompt {
  margin: 0;
}

.guest-restriction-message__login-link {
  border: 0;
  padding: 0;
  margin-left: 4px;
  background: transparent;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  font-size: inherit;
  line-height: inherit;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
